import styled from 'styled-components';
import { LoginInput } from '../components/items/joinInput';
import { Button } from '../components/items/Button';
import { Link, useHistory } from 'react-router-dom';
import { LoginSubscript } from '../utils/menu';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { loginSchema } from '../schema/loginSchema';
import { loginHandler } from '../api/login';
import { useSetRecoilState } from 'recoil';
import { authState } from '../atoms/authState'; // 새로 만든 authState 불러오기
import { AlertModal } from '../components/modal/alert.modal';

interface LoginForm {
  passid: string;
  password: string;
}

const Home = () => {
  const {
    register,
    handleSubmit,
    setFocus,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  // 모달
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const setAuth = useSetRecoilState(authState); // Recoil 상태 업데이트 함수
  const history = useHistory();

  const onSubmit = async (data: any) => {
    try {
      const response = await loginHandler({
        passid: data.passid,
        password: data.password,
      });

      const { accessToken, refreshToken, expire } = response.token;
      const { userType } = response;
      const expireTime = Date.now() / 1000 + expire;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('expire', String(expireTime));

      // Recoil 상태 업데이트
      if (userType) {
        // userType이 존재하는지 확인
        setAuth({
          isAuthenticated: true,
          userType: userType,
        });
      }

      // 로컬 스토리지에 사용자 유형 저장
      localStorage.setItem(
        'authState',
        JSON.stringify({ isAuthenticated: true, userType }),
      );

      if (userType === 'driver') {
        history.push('/userHome');
      } else if (userType === 'company') {
        history.push('/managerHome');
      }
    } catch (error: any) {
      console.error('로그인 실패:', error.message);
      setModalMessage(
        error.message ||
          '로그인에 실패했습니다. 아이디와 비밀번호를 확인해주세요.',
      );
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // Set focus on the username input field when the component mounts
  useEffect(() => {
    setFocus('passid');
  }, []);

  // 컴포넌트가 마운트될 때 로컬 스토리지에서 auth 상태 복원
  useEffect(() => {
    const storedAuth = localStorage.getItem('authState');
    if (storedAuth) {
      setAuth(JSON.parse(storedAuth));
    }
  }, [setAuth]);

  return (
    <Container>
      <div className="logo">
        <p>
          데일리 콘<span>(Daily Con)</span>
        </p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <LoginInput
          name="passid"
          label="아이디"
          placeholder="아이디 입력"
          type="text"
          errors={
            errors.passid?.message ? [errors.passid.message as string] : []
          }
          register={register}
        />
        <LoginInput
          name="password"
          label="비밀번호"
          placeholder="비밀번호 입력"
          type="password"
          btn
          errors={
            errors.password?.message ? [errors.password.message as string] : []
          }
          register={register}
        />
        <Button title="로그인" type="submit" />
      </form>
      <ul>
        {LoginSubscript.map((menu, index) => (
          <li key={index}>
            <Link
              to={{
                pathname: menu.path,
                search: menu.search, // 쿼리 문자열 추가
                state: {
                  auth: 'join',
                },
              }}
            >
              {menu.title}
            </Link>
          </li>
        ))}
      </ul>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default Home;

//style
const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 11.7rem;
  padding: 0 2rem;
  .logo {
    margin-bottom: 2.4rem;
    height: 8rem;
    p {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      font-size: 4rem;
      font-weight: 700;
      span {
        font-size: 1.6rem;
      }
    }
  }
  form {
    width: 100%;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }
  ul {
    display: flex;
    gap: 1.1rem;
    width: 100%;
    justify-content: center;
    align-items: center;
    li {
      display: flex;
      position: relative;
      width: 33%;
      a {
        display: flex;
        height: 4.2rem;
        width: 100%;
        align-items: center;
        justify-content: center;
        font-size: 1.3rem;
        font-weight: 400;
        color: ${(props) => props.theme.text222Color};
      }
      &:not(:first-child):after {
        content: '';
        width: 1px;
        height: 1.6rem;
        background-color: ${(props) => props.theme.loginHrColor};
        position: absolute;
        left: -0.6rem;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
`;
